import React, { useState } from "react";
import "./style.scss";
import DashboardModal from "../../components/Dashboard/DashboardModal";
import moment from "moment";
import { FormatUSD, formatDateInTimeZone } from "../../utils/helpers/string";
import { useDispatch, useSelector } from "react-redux";
import {
  accountMetrics,
  setLastPayment,
} from "../../utils/store/reducers/accountMetricsSlice";
import {
  accountList,
  setDefaultLoginId,
} from "../../utils/store/reducers/accountSlice";
import { Link } from "react-router-dom";
import { initiatePayment } from "../../utils/apis/payment";
import { resetRebillPayments } from "../../utils/apis/userApi";
import { returnErrors } from "../../utils/store/reducers/Error";
import RePaymentModal from "../RePaymentModal/RePaymentModal";
import { returnInfo } from "../../utils/store/reducers/Info";
import DeleteIcon from "../../assets/icons/delete.svg";
import { deleteAccount } from "../../utils/apis/accountsApi";
import ConfirmationModal from "../../components/BillingDetails/ConfirmationModal";
import { returnMessages } from "../../utils/store/reducers/Message";

function DataBox({ id, status, items, variant, activeTab, brokers }) {
  const [modalOpen, setModalOpen] = useState(false);
  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };
  const dispatch = useDispatch();
  const { login_id } = useSelector((state) => state?.accountList);
  const { idToken } = useSelector((state) => state?.auth);
  const [payload, setPayload] = useState({
    payment_type: "reset",
    payment_method: "checkout",
    account_id: items?.login_id,
    promo: "",
  });
  const [isLoadings, setIsLoading] = useState(false);
  const [openRepay, setOpenRepay] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleRenewAccount = async () => {
    setIsLoading(true);
    try {
      const response = await resetRebillPayments(idToken, payload);

      if (response.status < 399) {
        dispatch(setLastPayment(response?.data?.data));
        window.location.replace(response?.data.url);
      } else {
        dispatch(returnErrors(response.data.detail, 400));
      }
    } catch (error) {
      dispatch(returnErrors(error.message, error.response?.status || 500));
    } finally {
      setIsLoading(false);
      setOpenRepay(false);
    }
  };


  const [today, setToday] = useState(false);

  // Format status
  const formatStatus = (status) => {
    if (!status) return "N/A";

    // Handle already formatted statuses
    if (status.includes(" ")) {
      return status;
    }

    // Convert snake_case or camelCase to proper format
    const formatted = status
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before capital letters
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return formatted;
  };

  const handleDeleteClick = (loginId) => {
    setAccountToDelete(loginId);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = (force = false) => {
    if (isDeleting && !force) return;
    setDeleteModalOpen(false);
    setAccountToDelete(null);
  };

  const handleDelete = async () => {
    if (!accountToDelete) return;
    try {
      setIsDeleting(true);
      const payload = {
        login_id: accountToDelete,
      };
      await deleteAccount(payload, idToken);
      dispatch(returnMessages("Account deleted successfully.", 200));
      dispatch(
        accountList({
          idToken,
          params: {
            page: 1,
            page_size: 9,
            ...(brokers?.[activeTab]?.path && {
              platform: brokers[activeTab].path,
            }),
          },
        }),
      );
      closeDeleteModal(true);
    } catch (error) {
      dispatch(
        returnErrors(
          error?.response?.data?.detail || "Failed to delete account.",
          error?.response?.status || 500,
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const isQualifiedAndZeroPlan =
    items?.phase === "qualified" && items?.plan_name === "Zero Plan" || items?.plan_name === "Premium Plan";

  return (
    <>
      <div className="dashboard-box">
        <RePaymentModal
          open={openRepay}
          setOpen={setOpenRepay}
          handleSubmit={handleRenewAccount}
          isLoading={isLoadings}
          setPayload={setPayload}
          payload={payload}
          initialAmount={items?.no_activation_fee ? items.reset_price_2 : items.reset_price}
          size={""}
          id={items.login_id}
          items={items}
          isQualifiedAndZeroPlan={isQualifiedAndZeroPlan}
          isCoupon={items?.phase === "qualified" ? false : true}
        />
        <div className="dashboard-box-header">
          <div className="box-header-left">
            <span>Account</span>
            <p className="id">{items?.account_name}</p>
          </div>
          <div className="box-header-right">
            {items.phase === "evaluation" &&
            !items?.is_active &&
            items?.status.toLowerCase() === "passed" ? (
              <button className={"active-btn"}>Passed</button>
            ) : (
              <button
                className={items?.is_active ? "active-btn" : "inactive-btn"}
              >
                {items?.is_active ? "Active" : "Inactive"}
              </button>
            )}
            {((items.phase === "evaluation" &&
              items?.status.toLowerCase() != "passed") ||
              isQualifiedAndZeroPlan) && (
              <button
                onClick={() => {
                  const payoutDate = moment(items?.expiry_date).format(
                    "DD-MM-YYYY",
                  );
                  const today = moment().format("DD-MM-YYYY");

                  if (payoutDate === today) {
                    setToday(true);
                  }

                  if (payoutDate === today) {
                    dispatch(
                      returnInfo(
                        "Your rebill is today: do you want to continue to trade at the same level or reset the account?",
                        200,
                      ),
                    );
                  }
                  setOpenRepay(true);
                }}
                className="inactive-btn"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        <div className="dashboard-box-items">
          <div className="item">
            <span>phase</span>
            <p style={{ textTransform: "capitalize" }}>{items?.phase}</p>
          </div>
          <div className="item">
            <span>Status</span>
            <p>{formatStatus(items?.status)}</p>
          </div>
          <div className="item">
            <span>Balance</span>
            <p>{FormatUSD(items?.account_balance_equity?.balance)}</p>
          </div>
          <div className="item">
            <span>Platform</span>
            <p>{items?.trading_platform}</p>
          </div>
          <div className="item">
            <span>Started</span>
            <p>{moment(items?.created_at).format("DD-MM-YYYY")}</p>
          </div>
          {items?.phase !== "qualified" && (
            <div className="item">
              <span>Ending</span>
              <p>
                {items?.expiry_date
                  ? moment(items?.expiry_date).format("DD-MM-YYYY")
                  : "N/A"}
              </p>
            </div>
          )}
          {items?.phase === "qualified" && (
            <>
              {items?.plan_name === "Standard Plan" && (
                <div className="item">
                  <span>Next Payout Date</span>
                  <p>
                    {items?.expiry_date
                      ? moment(items?.next_payout_date).format("DD-MM-YYYY")
                      : "N/A"}
                  </p>
                </div>
              )}
            </>
          )}
          {items?.phase === "qualified" &&
            (items?.plan_name === "Advanced Plan" ||
              items?.plan_name === "Zero Plan") && (
              <div className="item">
                <span>Remaining Winning days for next payout</span>
                <p>{items?.remaining_wining_days}</p>
              </div>
            )}
          <div className="item">
            <span>Plan Name</span>
            <p>{items?.plan_name}</p>
          </div>
        </div>

        {variant === "btns" && (
          <div className="btns">
            <button className="info-btn" onClick={toggleModal}>
              Account Info
            </button>
            <Link
              to="/account-stats"
              className="info-btn new"
              onClick={() => {
                if (login_id !== items?.login_id) {
                  dispatch(
                    accountMetrics({ id: items?.login_id, idToken: idToken }),
                  );
                  dispatch(setDefaultLoginId(items?.login_id));
                }
              }}
            >
              Account Metrics
            </Link>
            <button
              className="delete-btn"
              onClick={() => handleDeleteClick(items?.login_id)}
            >
              <img src={DeleteIcon} alt="DeleteIcon" />
            </button>
          </div>
        )}
      </div>
      <DashboardModal isOpen={modalOpen} item={items} onClose={toggleModal} />
      <ConfirmationModal
        open={deleteModalOpen}
        handleConfirm={handleDelete}
        handleClose={closeDeleteModal}
        loading={isDeleting}
        message={
          <>
            Are you sure you want to delete{" "}
            <strong>{items?.account_type?.toUpperCase()}</strong> account{" "}
            <strong>{items?.account_name}</strong> with balance{" "}
            <strong>{FormatUSD(items?.account_balance_equity?.balance)}</strong>
            ?
            <br />
            {items?.phase === "evaluation" && (
              <>
                <br />
                <span
                  style={{
                    fontSize: "14px",
                    display: "block",
                    marginBottom: "15px",
                    lineHeight: "1.5",
                  }}
                >
                  <strong>Note:</strong> Deleting the account using this option{" "}
                  <strong>WILL NOT CANCEL</strong> your subscriptions.
                  Subscriptions must be managed from the Billing Details page.
                  This option will{" "}
                  <strong>only remove and close your trading account</strong>{" "}
                  and does not affect any active subscriptions.
                </span>
              </>
            )}
            <span style={{ color: "#d32f2f" }}>
              This action cannot be undone.
            </span>
          </>
        }
      />
    </>
  );
}

export default DataBox;
